import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api/client";
import { translateServerField } from "@/lib/api/server-messages";
import { apiFieldErrorId } from "./api-field-errors";

interface ApiFieldErrorsProps {
  error: ApiError | Error | null | undefined;
  fieldLabels?: Record<string, string>;
  className?: string;
  embedded?: boolean;
  idPrefix?: string;
}

function labelFor(fieldLabels: Record<string, string> | undefined, field: string): string {
  if (fieldLabels && Object.prototype.hasOwnProperty.call(fieldLabels, field)) {
    return fieldLabels[field];
  }
  return translateServerField(field) ?? field;
}

export function ApiFieldErrors({
  error,
  fieldLabels,
  className = "",
  embedded = false,
  idPrefix,
}: ApiFieldErrorsProps) {
  const { t } = useTranslation();

  if (!error) return null;

  const apiError = error instanceof ApiError ? error : undefined;
  const fieldErrors = apiError?.errors ?? [];
  const hasFieldErrors = fieldErrors.length > 0;
  const showSentence = Boolean(apiError?.error) || !hasFieldErrors;

  const body = (
    <>
      {showSentence && <p>{apiError?.error ?? t("common.error")}</p>}
      {hasFieldErrors && (
        <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4">
          {fieldErrors.map((fieldError, index) => (
            <li
              key={index}
              id={idPrefix ? apiFieldErrorId(idPrefix, fieldError.field, index) : undefined}
            >
              <span className="font-medium">{labelFor(fieldLabels, fieldError.field)}</span>:{" "}
              {fieldError.message}
            </li>
          ))}
        </ul>
      )}
    </>
  );

  if (embedded) {
    if (className) {
      return <div className={className}>{body}</div>;
    }
    return body;
  }

  return (
    <div role="alert" className={`text-xs text-danger ${className}`}>
      {body}
    </div>
  );
}
